import Vue from 'vue';
import { getStoreBuilder, BareActionContext } from 'vuex-typex';
import { AxiosError } from 'axios';
import authAxios from '@/state/auth-axios'; // Use this to make okta-auth'd axios requests
import rootStore from '@/state/store';

// === STATE - Prepare initial state, store builder
export const initialOptionsState: IOptionsStore = {
	employeeId: [],
	officeId: [],
	partner: [],
	serviceStatus: [],
	// These values are preset but can be overridden from the config in overrideOptions
	boolean: [
		{ value: "yes", label: "Yes" },
		{ value: "no", label: "No" }
	],
	interactionType: [
		{ value: "inbound", label: "Inbound" },
		{ value: "formsubmission", label: "Form Submission" }
	],
	partnerType: [
		{ value: "affiliate", label: "Affiliate" },
		{ value: "channel", label: "Channel" },
		{ value: "ecomm", label: "Ecomm" },
		{ value: "nis", label: "NIS" },
		{ value: "proDealer", label: "Pro Channel" },
		{ value: "vdp", label: "VDP" }
	],
	questionsPhoneNumber: [
		{ value: "855-737-3595", label: "855-737-3595 AMI Phone" },
		{ value: "844-321-3972", label: "844-321-3972 Affiliates" },
		{ value: "855-824-3723", label: "855-824-3723 Pro Dealer" },
		{ value: "866-248-3618", label: "866-248-3618 DirectSat" }
	]
};

// Prepare store builder for use throughout
const storeBuilder = getStoreBuilder<IRootStore>();
const moduleBuilder = storeBuilder.module<IOptionsStore>(
	'options',
	initialOptionsState,
);

// === GETTERS - Like computed data or used to get a part of the data or return it in a specific way and cache it

// === MUTATIONS - Synchronous work that mutates the data store. Should be quick
interface IOptionsMutationParams {
	which: string;
	options: ISelectOption[];
}
const SET_OPTIONS_MUTATION = (
	state: IOptionsStore,
	data: IOptionsMutationParams,
) => {
	Vue.set(state, data.which, data.options); // For reactivity
};

// === ACTIONS - Asynchronous work that gets data, changes the data for storage, and handles errors.
const GET_OPTIONS_ACTION = (
	{}: BareActionContext<IOptionsStore, IRootStore>
): Promise<IOptionsStore> => {
	if (rootStore.getUserIsSuperAdmin) {
		fetchOfficeIdOptions()
			.then((data: ISelectOption[]) => {
				optionsStore.commitSetOptions({which: 'officeId', options: data});
				return data;
			});
		fetchEmployeeIdOptions()
			.then((data: ISelectOption[]) => {
				optionsStore.commitSetOptions({which: 'employeeId', options: data});
				return data;
			});
	}
	fetchServiceStatusOptions()
		.then((data: ISelectOption[]) => {
			optionsStore.commitSetOptions({which: 'serviceStatus', options: data});
			return data;
		});
	return Promise.resolve(rootStore.state.selectOptions);
};
const OVERRIDE_OPTIONS_ACTION = (
	{}: BareActionContext<IOptionsStore, IRootStore>,
	options: {string: ISelectOption[]}
): Promise<any> => {
	for (const [key, value] of Object.entries(options)) {
		optionsStore.commitSetOptions({which: key, options: value});
	}
	return Promise.resolve(options);
};

// === SERVICES
const fetchOfficeIdOptions = (): Promise<ISelectOption[]> => {
	// prepare url for request
	const config = rootStore.state.config as ISiteConfig;
	const url = `${config.partnerServiceUrl}/offices`;

	// Create request and return promise for processed data
	return authAxios.get(url)
		.then((response) => {
			const { data }: { data: any } = response; // remove headers

			return Promise.resolve(data);
		})
		.catch((err: AxiosError) => {
			throw err;
		});
};
const fetchEmployeeIdOptions = (): Promise<ISelectOption[]> => {
	// prepare url for request
	const config = rootStore.state.config as ISiteConfig;
	const url = `${config.partnerServiceUrl}/employees`;

	// Create request and return promise for processed data
	return authAxios.get(url)
		.then((response) => {
			const { data }: { data: any } = response; // remove headers

			return Promise.resolve(data);
		})
		.catch((err: AxiosError) => {
			throw err;
		});
};
const fetchServiceStatusOptions = (): Promise<ISelectOption[]> => {
	const data: any = [
		{ value: "Active", label: "Active" },
		{ value: "Cancelled", label: "Cancelled" },
		{ value: "Customer Moved", label: "Customer Moved" },
		{ value: "Customer Upgraded", label: "Customer Upgraded" },
		{ value: "Never Installed", label: "Never Installed" },
		{ value: "Pending Install", label: "Pending Install" },
		{ value: "Pending Takeover", label: "Pending Takeover" },
		{ value: "Pending to Cancel", label: "Pending to Cancel" },
		{ value: "Unknown", label: "Unknown" }
	];
	return Promise.resolve(data);
};

// === EXPORTS - Modules state or storage handling. Gets combined with the root store on compile or run.
// Prepare this module's state for export
const stateGetter = moduleBuilder.state();

// exported module interface
const optionsStore = {
	// state
	get state() { return stateGetter(); },

	// getters

	// mutations
	commitSetOptions: moduleBuilder.commit(SET_OPTIONS_MUTATION, 'SET_OPTIONS'),

	// action
	dispatchGetOptions: moduleBuilder.dispatch(
		GET_OPTIONS_ACTION,
		'getOptions',
	),
	dispatchOverrideOptions: moduleBuilder.dispatch(
		OVERRIDE_OPTIONS_ACTION,
		'overrideOptions',
	),
};

// Export default store object
export default optionsStore;
